import {
  createContext,
  useContext,
  useId,
  useRef,
  type KeyboardEvent,
  type PropsWithChildren,
} from 'react';
import { cn } from '@lib/utils/cn';

interface TabsCtx {
  value: string;
  onChange: (v: string) => void;
  baseId: string;
  registerTab: (el: HTMLButtonElement | null, value: string) => void;
}

const Ctx = createContext<TabsCtx | null>(null);

function useTabs() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('Tabs components must be used within <Tabs>');
  return ctx;
}

export function Tabs({
  value,
  onChange,
  className,
  children,
}: PropsWithChildren<{ value: string; onChange: (v: string) => void; className?: string }>) {
  const baseId = useId();
  const tabsRef = useRef<Map<string, HTMLButtonElement>>(new Map());

  const registerTab = (el: HTMLButtonElement | null, v: string) => {
    if (el) tabsRef.current.set(v, el);
    else tabsRef.current.delete(v);
  };

  return (
    <Ctx.Provider value={{ value, onChange, baseId, registerTab }}>
      <TabsRefProvider tabsRef={tabsRef}>
        <div className={className}>{children}</div>
      </TabsRefProvider>
    </Ctx.Provider>
  );
}

const TabsRefCtx = createContext<{ tabsRef: React.MutableRefObject<Map<string, HTMLButtonElement>> } | null>(null);
function TabsRefProvider({
  tabsRef,
  children,
}: PropsWithChildren<{ tabsRef: React.MutableRefObject<Map<string, HTMLButtonElement>> }>) {
  return <TabsRefCtx.Provider value={{ tabsRef }}>{children}</TabsRefCtx.Provider>;
}
function useTabsRef() {
  const ctx = useContext(TabsRefCtx);
  if (!ctx) throw new Error('TabList must be inside <Tabs>');
  return ctx.tabsRef;
}

export function TabList({ className, children }: PropsWithChildren<{ className?: string }>) {
  const { value, onChange } = useTabs();
  const tabsRef = useTabsRef();
  const isRtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const keys = Array.from(tabsRef.current.keys());
    if (!keys.length) return;
    const idx = keys.indexOf(value);
    let nextIdx = -1;
    switch (e.key) {
      case 'ArrowRight':
        nextIdx = isRtl ? idx - 1 : idx + 1;
        break;
      case 'ArrowLeft':
        nextIdx = isRtl ? idx + 1 : idx - 1;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = keys.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    if (nextIdx < 0) nextIdx = keys.length - 1;
    if (nextIdx >= keys.length) nextIdx = 0;
    const nextVal = keys[nextIdx];
    onChange(nextVal);
    queueMicrotask(() => tabsRef.current.get(nextVal)?.focus());
  };

  return (
    <div
      role="tablist"
      onKeyDown={onKeyDown}
      className={cn('flex gap-1 border-b border-border overflow-x-auto', className)}
    >
      {children}
    </div>
  );
}

export function Tab({
  value,
  disabled,
  className,
  children,
}: PropsWithChildren<{ value: string; disabled?: boolean; className?: string }>) {
  const { value: active, onChange, baseId, registerTab } = useTabs();
  const selected = active === value;
  return (
    <button
      ref={(el) => registerTab(el, value)}
      role="tab"
      type="button"
      id={`${baseId}-tab-${value}`}
      aria-controls={`${baseId}-panel-${value}`}
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      disabled={disabled}
      onClick={() => onChange(value)}
      className={cn(
        'relative px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors',
        'hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 focus-visible:rounded',
        selected ? 'text-brand' : 'text-fg-muted',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      {children}
      {selected && (
        <span className="absolute inset-x-0 -bottom-px h-0.5 bg-brand rounded-t" />
      )}
    </button>
  );
}

export function TabPanel({
  value,
  className,
  children,
}: PropsWithChildren<{ value: string; className?: string }>) {
  const { value: active, baseId } = useTabs();
  if (active !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`${baseId}-panel-${value}`}
      aria-labelledby={`${baseId}-tab-${value}`}
      className={cn('py-4 animate-fade-in', className)}
    >
      {children}
    </div>
  );
}
